import React, { useContext, useRef, useEffect } from 'react'
import { motion, useInView, useAnimationControls } from 'framer-motion/dist/framer-motion'

import { UIContext } from '../../../Context/UIContext'
import { DataContext } from '../../../Context/DataContext'

import './Team.scss'

import Section from '../../Section'
import TeamCard from './TeamCard'
import PartnerCard from './PartnerCard'
import MemberModal from './MemberModal'
import MotionModal from '../../Modal'

const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.5,
            delayChildren: 1,
            type: "spring",
        }
    }
}

const item = {
    hidden: { opacity: 0, y: '25%' },
    show: { opacity: 1, y: 0, transition: { duration: 1 } }
}

const Team = ({ data }) => {
    const { title, content } = data
    const controlsTeam = useAnimationControls()
    const controlsTitle = useAnimationControls()
    const controlsPartners = useAnimationControls()

    const { strings } = useContext(DataContext)
    const { modalVisible, setSelectedModal, memberModalVisible, setSelectedMember, setMemberModalRef, isTablet } = useContext(UIContext)

    const gridTeam = useRef(null)
    const teamVisible = useInView(gridTeam, { once: true, amount: isTablet ? 0.3 : 0.6 })

    const titlePartners = useRef(null)
    const titleVisible = useInView(titlePartners, { once: true, amount: 1 })

    const gridPartners = useRef(null)
    const partnersVisible = useInView(gridPartners, { once: true, amount: isTablet ? 0.3 : 0.6 })

    useEffect(() => {
        if (teamVisible) {
            controlsTeam.start("show")
        }
        return controlsTeam.stop
    }, [teamVisible, controlsTeam])

    useEffect(() => {
        if (titleVisible) {
            controlsTitle.start({ opacity: 1, y: 0, transition: { duration: 1, type: "spring" } })
        }
        return controlsTitle.stop
    }, [titleVisible, controlsTitle])

    useEffect(() => {
        if (partnersVisible) {
            controlsPartners.start("show")
        }
        return controlsPartners.stop
    }, [partnersVisible, controlsPartners])

    useEffect(() => {
        if (!memberModalVisible) {
            setSelectedMember(null)
            setMemberModalRef(null)
        }
    }, [memberModalVisible, setMemberModalRef, setSelectedMember])

    useEffect(() => {
        if (!modalVisible) {
            setSelectedModal(null)
        }
    }, [modalVisible, setSelectedModal])

    return (
        <>
            <Section
                name="mga-team"
                className="mga-team-section"
                motionProps={{
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    exit: { opacity: 0, transition: { duration: 1 }},
                    transition: { duration: 1 }
                }}
            >
                <div className="mga-section__wrapper">
                    <div className="mga-team-section__group">
                        <motion.h1
                            initial={{ opacity: 0, x: '-35%' }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1 }}
                        >
                            { title }
                        </motion.h1>
                        <motion.div
                            ref={gridTeam}
                            className="mga-team-section__grid"
                            initial="hidden"
                            variants={container}
                            animate={controlsTeam}
                        >
                            {content.team?.map((member, i) =>
                                <TeamCard 
                                    key={`member-${i}`}
                                    data={member}
                                    variants={item}
                                />
                            )}
                        </motion.div>
                    </div>
                    <div className="mga-team-section__group">
                        <motion.h3
                            ref={titlePartners}
                            initial={{ opacity: 0, y: '250%' }}
                            animate={controlsTitle}
                        >
                            { strings.partners }
                        </motion.h3>
                        <motion.div
                            ref={gridPartners}
                            initial="hidden"
                            variants={container}
                            animate={controlsPartners}
                            className="mga-team-section__grid"
                        >
                            { content.partners?.map((partner, i) => 
                                <PartnerCard
                                    key={`partner-${i}`}
                                    data={partner}
                                    variants={item}
                                />
                            )}
                        </motion.div>
                    </div>
                </div>
            </Section>
            <MemberModal key="team-member-modal" />
            <MotionModal key="partner-global-modal" showTitle={true} className="partner-modal" />
        </>
    )
}

export default React.memo(Team)